import { jsPDF } from "jspdf";
import {
    formatCurrencyInCents,
    formatDate,
    formatDocument,
    formatPhone,
    resolvePublicMediaUrl,
} from '../../helpers/formatters';

function hasUserLink(value) {
    if (value == null) {
        return false;
    }
    return String(value).trim() !== '';
}

// Função auxiliar para carregar imagem como base64 com timeout e retry
const getBase64FromUrl = async (url, retries = 3, timeout = 10000) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
        try {
            console.log(`Tentativa ${attempt} de carregar logo: ${url}`);

            // Criar um AbortController para timeout
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), timeout);

            const response = await fetch(url, {
                signal: controller.signal,
                cache: 'no-cache' // Não usar cache para evitar problemas com S3
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const blob = await response.blob();

            // Log para debug
            console.log(`Blob carregado - Tipo: ${blob.type}, Tamanho: ${blob.size} bytes`);

            // Verificar se o arquivo não está vazio
            if (blob.size === 0) {
                throw new Error('Arquivo está vazio');
            }

            // Verificar se é uma imagem válida pelo conteúdo (mais confiável)
            const arrayBuffer = await blob.arrayBuffer();
            const uint8Array = new Uint8Array(arrayBuffer);

            // Verificar assinaturas de arquivos de imagem comuns
            const isPNG = uint8Array[0] === 0x89 && uint8Array[1] === 0x50 && uint8Array[2] === 0x4E && uint8Array[3] === 0x47;
            const isJPEG = uint8Array[0] === 0xFF && uint8Array[1] === 0xD8 && uint8Array[2] === 0xFF;
            const isGIF = uint8Array[0] === 0x47 && uint8Array[1] === 0x49 && uint8Array[2] === 0x46;
            const isWEBP = uint8Array[8] === 0x57 && uint8Array[9] === 0x45 && uint8Array[10] === 0x42 && uint8Array[11] === 0x50;

            if (!isPNG && !isJPEG && !isGIF && !isWEBP) {
                console.warn(`Arquivo pode não ser uma imagem válida. Tipo MIME: ${blob.type || 'desconhecido'}, Primeiros bytes: ${Array.from(uint8Array.slice(0, 8)).map(b => b.toString(16).padStart(2, '0')).join(' ')}`);
                // Não falhar, apenas avisar - deixar o jsPDF tentar processar
            } else {
                console.log('Imagem válida detectada pelo conteúdo');
            }

            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.onloadend = () => {
                    if (reader.result) {
                        console.log(`Logo carregada com sucesso na tentativa ${attempt}`);
                        resolve(reader.result);
                    } else {
                        reject(new Error('Falha ao converter imagem para base64'));
                    }
                };
                reader.onerror = () => reject(new Error('Erro ao ler arquivo'));
            });

        } catch (error) {
            console.warn(`Tentativa ${attempt} falhou:`, error.message);

            if (attempt === retries) {
                console.error("Todas as tentativas de carregar a logo falharam:", error);
                return null;
            }

            // Aguardar um pouco antes da próxima tentativa
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
    }

    return null;
};

// Função para gerar o PDF do recibo
export const generatePDF = async (order) => {
    const doc = new jsPDF();
    const paymentDetails = order.payment_details ?? {};
    let isPaid = Boolean(paymentDetails.paid);
    let user = {};
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
        user = JSON.parse(storedUser);
    }

    const shipping = order.shipping_method ?? {};
    const customer = order.customer ?? {};

    const iconWhatsApp = "data:image/png;base64,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";
    const iconInstagram = "data:image/png;base64,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"
    const iconSite = "data:image/png;base64,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";
    const iconTikTok = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAEAAAABACAYAAACqaXHeAAAAAXNSR0IArs4c6QAABt9JREFUeF7Vm1eoJUUQhj8zmOOu+GDGLOYHFcODCUXQXX0wJxQUzBlxFQUxxxdFMSfMggH1yRzWsGYFdRUVVNYccDHOd5i5nD135kxPT8899xbcl3u6q6r/7q6qrqpZiO5pGrAjsDGwAbAesAKwPLB0Lv434CfgR+Bj4CPgfeB54LsuVVyoI+bbZAs5ANg1X3isnP9yIJ4G7gFeT61vrGJleiwLHAMcCWyYWtGc3wfALcANwK8pZKQAYMXs6J4IHJ8f7RR61fHwqlybybsmvzZ14yt/bwOAcw/J7vHlwCrRGrSb+ANwAXAd8G8Mq1gA1gVuA7aLEdrBnBeBw7IN+bQp7xgA9gVuzq14U3ldjtcmaIPubSKkCQALA1dk7uqkJgJGMPZK4PTQKxEKwOLArblrG8GaGou8Gzgc+KtuZggALv5hYM86ZpPs98cBr+tQEOoA8Hd3/tBJtrhQdQyeDh52HeoAuGoK3Pk6MHTT2oRSGgbA/sB9ddynyO8H5qH0OHWrANDPvwEY3nZNP2fxhLF+Ge2cKMhSxpbZVf5sUEgZAP7vhQkMct7JjOxmFQBofPdJtAO+LHcCfGCNURkARwE3JRIawmYYAPr0k0OYBI7RNRrBVgLgw8b3+MqBDFMMGwaAz+mq6xEj29zC+nnuoTd/8AT4sDg3hnOLOcMAWAzwCaxNSkXn5Q+ocQBo8D6fwCdtsaBhADhmBvBgqtUDviDXLPIJ/SfgNOCyhIJCWdUBIJ+r85xDKM+6cadmHk77ssAVeBfYpG5mB7+HAOBGeXT9S0FeK3OUYwCYw3stBecIHiEAFGw1ipcAW0TIGZxiXPBWcQVSu5sm+jUBoOC7aRar7JLlB9cADor0Wr0QuQDgveJINNE80dgYAPpFzwa2jtBljidJAMzbf1PiEiN4Rk0ZFQBGhNMEYL9s9++PUj3NpFEBoPYzC+t6fpq1RHGpAmAp4NIsoXFOf+RWIiH2CshqlgCYPrKK0wUZWD0JvAJ8CfyRJVRXH3hmVwGwDPAL8G3us03EzksMwF0CYLlpq8Srd1Hu3BMl2Zjpuc0pRNYBUIybn4P5bP5aFdDvgZcjjaB8ZwuAu6Q7SUEWJwxWLgb+rmAYC0CVfv8Ai0QqP1cAPFYrRTLon+aCzbzUGdRQAHybmMjokuYJgEfLzG9bOha4PoDJZAJgfioAvOt7BSzeIaEALFdj/QPFDR3WA6DtFfDo+17/okKUFSXTWjvk+T0N1wl9Y6uM4EQA0LsCbY2gtbgqN7pWXlSpyvmJwygB6BnBtm7Q3X20ZPdNq70KrF1zVqsAsIXGPoAuqecG2wRCxtP2+5RZ64syA3t2gPajBKAXCOm3Y0Nho7RVKxb5NbDaJAegFwrPzDJBDwQoWjbETq6yLJJxvPX6utKbPN8GNi9h7skyf9clzWj7HLYjoyxjuyhg69sSAdo/llWh9i4ZpwEdV8kJ4Bc6xKh1erFD3kOzLE3Jx40Rm+HoIFld2j6A4awslL2wZNy2wEsB82OHjCVEZNAmJbYR8GGJFvYTWKMfRr729BLGBoNk55nZ4K7IDPgZxQkwpeS7Ooa09D5+ymiYJ/gzD5CeqpjrM3qPGIUC55hYndNvpDRo7mZTsq1VQ1h2DeRlkKSnsSQl6TqfyV3kmxXCvP+fZBUco8guyBxo78qnKowckXeSDFPWXkJdpobt95pV3ZF3dnSxeHmWFkbMwBjP636aku8Jj9RXTSeWjN89T3yEuNAYcdobS2N6qXF+2oAotvpi2mu3lj289hjb9BizCaFgLOB1BlFWsOXx2NZX01O+DWJa3K1O6TViZYcAYOSqLRoL3cuOmffZBGQsWWM4LovuHhnsxqhgaLBkYdadSZGYGaa37bS39w+oapGxnSQkiBkmTLdqW7tAlPl5d8I+PrvMQ94MsRtSzHsOsOeotkXGCevkTVImJdqSAjWuWn/DzyVz/maGJor8GsVi6NxBgcMs7agrRinB8cH3UBnDOldjc/QpKTUZAS+rS2dWya0DwN81iHZXTUUy2eNHHZUfU9QB4KJtVNKQTbVmaZ/Z9he1apYudt33vRbdD6KmAt2Z65qkXb5YsKfF+6TPnqykx/GZe1ZgDBKUshpcrJGedqHLcDUGYHMLRzdt8A6xAWXKmMTwOwKLHZOBDHI01OP8fJ1ysQDI17laWI+cbTajIJOmJmRuDD3yg0q2AaDgZQHDUpcpLHuNJ4IMrf1o0o8nW1WQUwBQLNgPoYtPZ3tNiB2QmRztjzvee8+3pZQA9OticsReARsbTT3FprYMYMxYm0IzqDGTm5S6AqBfSWuEfj5vvtGEh5/Pe1XKPp/3Tg9+Pl/WF5QMhP8BytVGc/6p4BcAAAAASUVORK5CYII=";

    // Adicionar logo no topo (se existir)
    if (user.profile_picture) {
        console.log("Iniciando carregamento da logo...");
        const logoBase64 = await getBase64FromUrl(resolvePublicMediaUrl(user.profile_picture));
        if (logoBase64) {
            try {
                // Aguardar o carregamento da imagem antes de continuar
                await new Promise((resolve, reject) => {
                    const img = new Image();
                    img.onload = () => {
                        try {
                            doc.addImage(logoBase64, 'JPEG', 80, 10, 50, 50);
                            console.log("Logo adicionada ao PDF com sucesso");
                            resolve();
                        } catch (error) {
                            console.warn("Erro ao adicionar logo ao PDF:", error);
                            resolve(); // Continua mesmo com erro
                        }
                    };
                    img.onerror = () => {
                        console.warn("Logo carregada mas inválida, prosseguindo sem ela");
                        resolve(); // Continua mesmo com erro
                    };
                    img.src = logoBase64;
                });
            } catch (error) {
                console.warn("Erro ao processar logo:", error);
            }
        } else {
            console.warn("Logo não foi carregada após todas as tentativas, prosseguindo sem ela.");
        }
    }

    // Adicionar título
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text("Recibo de Venda", 105, 70, null, null, "center");

    // Parágrafo inicial
    doc.setFont("helvetica", "normal");
    doc.setFontSize(12);

    let storeMessage = `Venda realizada por ${user.store_name ?? ''} em ${formatDate(order.created_at, false)}, na importância de ${formatCurrencyInCents(order.amount)} conforme detalhes abaixo.`;
    if (isPaid) {
        const paidAt = paymentDetails.payment_date ? formatDate(paymentDetails.payment_date, false) : formatDate(order.created_at, false);
        const paidFromMethods = Array.isArray(order.payment_methods) && order.payment_methods.length > 0
            ? order.payment_methods.reduce((sum, method) => sum + (Number(method.amount) || 0), 0)
            : 0;
        const paidAmount = paidFromMethods > 0
            ? paidFromMethods
            : Math.max(0, (Number(order.amount) || 0) - (Number(order.discount) || 0));
        storeMessage = `Em ${paidAt}, ${user.store_name ?? ''} recebeu de ${customer.name ?? ''} a importância de ${formatCurrencyInCents(paidAmount)} referente à venda dos produtos conforme detalhes abaixo.`;
    }

    const splitMessage = doc.splitTextToSize(storeMessage, 170);
    doc.text(splitMessage, 20, 80);

    let yPosition = 100;
    doc.setFontSize(12);
    doc.text(`Cliente: ${customer.name ?? ''}`, 20, yPosition);
    yPosition += 5;
    doc.text(`Documento: ${formatDocument(customer.document ?? customer.document_number ?? '') || "Não informado"}`, 20, yPosition);
    yPosition += 5;
    doc.text(`E-mail: ${customer.email || "Não informado"}`, 20, yPosition);
    yPosition += 5;
    doc.text(`Telefone: ${formatPhone(customer.phone) || "Não informado"}`, 20, yPosition);
    yPosition += 10;

    // Produtos
    doc.text("Produtos:", 20, yPosition);
    yPosition += 5;
    const products = Array.isArray(order.products) ? order.products : [];
    products.forEach((product) => {
        doc.text(
            `${product.name ?? ''} - ${product.quantity} x ${formatCurrencyInCents(product.base_price)}: ${formatCurrencyInCents(product.base_price * product.quantity)}`,
            20,
            yPosition
        );
        yPosition += 5;
    });

    // Forma de entrega
    yPosition += 5; // Espaçamento extra
    doc.text(`Forma de entrega: ${shipping.name ?? 'Não informado'}`, 20, yPosition);

    if ((shipping.cost ?? 0) > 0) {
        yPosition += 5; // Ajuste da posição para nova linha
        doc.text(`Valor: ${formatCurrencyInCents(shipping.cost)}`, 20, yPosition);
    }

    if ((shipping.delivery_days ?? 0) > 0) {
        let du = "dias úteis";
        if (shipping.delivery_days === 1) {
            du = "dia útil";
        }
        yPosition += 5; // Ajuste da posição para nova linha
        doc.text(`Prazo: ${shipping.delivery_days} ${du}`, 20, yPosition);
    }

    yPosition += 10; // Espaçamento para o próximo bloco

    // Forma de pagamento
    doc.text("Forma de pagamento:", 20, yPosition);
    yPosition += 5;

    if (Array.isArray(order.payment_methods) && order.payment_methods.length > 0) {
        order.payment_methods.forEach((method) => {
            let paymentDetails = method.name ?? '';

            // Verifica se a forma de pagamento é "Crédito" e exibe as parcelas
            if ((method.name ?? '').toLowerCase() === "crédito" && method.installments) {
                if (method.installments === 1) {
                    paymentDetails += " (à vista)";
                } else {
                    paymentDetails += ` (${method.installments}x)`;
                }
            }

            // Exibe cada forma de pagamento em uma nova linha
            doc.text(`${paymentDetails}`, 20, yPosition);
            yPosition += 5;
        });
    }
    yPosition += 10;

    doc.setFont("helvetica", "bold");
    const productsSubtotal = products.reduce((sum, product) => {
        const qty = Number(product.quantity) || 0;
        const price = Number(product.base_price) || 0;
        return sum + (qty * price);
    }, 0);
    const discountCents = Number(order.discount) || 0;
    const commissionCents = Number(order.commission) || 0;
    const paidTotal = Array.isArray(order.payment_methods) && order.payment_methods.length > 0
        ? order.payment_methods.reduce((sum, method) => sum + (Number(method.amount) || 0), 0)
        : Math.max(0, (Number(order.amount) || 0) - discountCents);

    if (productsSubtotal > 0 && productsSubtotal !== (Number(order.amount) || 0)) {
        doc.setFont("helvetica", "normal");
        doc.text(`Subtotal produtos: ${formatCurrencyInCents(productsSubtotal)}`, 20, yPosition);
        yPosition += 5;
        doc.setFont("helvetica", "bold");
    }
    if (discountCents > 0) {
        doc.setFont("helvetica", "normal");
        doc.text(`Desconto: -${formatCurrencyInCents(discountCents)}`, 20, yPosition);
        yPosition += 5;
        doc.setFont("helvetica", "bold");
    }
    if (commissionCents > 0) {
        doc.setFont("helvetica", "normal");
        doc.text(`Comissão: ${formatCurrencyInCents(commissionCents)}`, 20, yPosition);
        yPosition += 5;
        doc.setFont("helvetica", "bold");
    }
    doc.text(`Total: ${formatCurrencyInCents(paidTotal > 0 ? paidTotal : order.amount)}`, 20, yPosition);
    yPosition += 10;

    if (order.observations) {
        doc.setFont("helvetica", "normal");
        const obsLines = doc.splitTextToSize(`Observações: ${order.observations}`, 170);
        doc.text(obsLines, 20, yPosition);
        yPosition += 5 * obsLines.length + 5;
    }

    // Data da venda
    doc.setFont("helvetica", "normal");
    doc.text(`Data da Venda: ${formatDate(order.created_at, false)}`, 20, yPosition);
    yPosition += 10;

    if (!isPaid) {
        // Configuração para a caixa
        const boxX = 20;
        const boxY = yPosition;
        const boxWidth = 170;
        const boxHeight = 20;

        // Desenha o contorno da caixa
        doc.setDrawColor(255, 0, 0); // Cor vermelha para o contorno
        doc.setLineWidth(0.5);
        doc.rect(boxX, boxY, boxWidth, boxHeight); // x, y, largura, altura

        // Adiciona o texto dentro da caixa
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(255, 0, 0); // Texto vermelho
        const disclaimer = "Atenção: O pagamento desta venda está pendente.";
        doc.text(disclaimer, boxX + 5, boxY + 12); // Posição do texto dentro da caixa

        yPosition += boxHeight + 10; // Espaçamento adicional após o disclaimer
    }

    yPosition += 20;
    doc.setTextColor(0, 0, 0);
    const iconY = 250;
    let iconOffsetY = 0;  // Variável para o deslocamento vertical
    const iconX = 40;
    const iconSize = 8; // Reduzindo o tamanho do ícone
    const textFontSize = 7; // Font menor para o texto

    // Função para adicionar ícones e texto na linha
    function addIconWithText(icon, text, x, y) {
        const label = text == null ? '' : String(text).trim();
        if (!label) {
            return;
        }
        doc.addImage(icon, 'PNG', x, y, iconSize, iconSize);
        doc.setFontSize(textFontSize);  // Define o tamanho da fonte menor
        doc.textWithLink(label, x + iconSize + 2, y + 5, { url: label });
    }

    const line1 = []
    if (hasUserLink(user.site)) {
        line1.push({ site: user.site });
    }

    let currentCount = 0;

    if (hasUserLink(user.site)) {
        currentCount++;
        addIconWithText(iconSite, String(user.site).trim(), iconX, iconY + iconOffsetY);
    }
    if (hasUserLink(user.whatsapp)) {
        currentCount++;
        if (currentCount === 2) {
            doc.text("", iconX + iconSize + 40, iconY + iconOffsetY + 5);
            addIconWithText(iconWhatsApp, String(user.whatsapp).trim(), iconX + 80, iconY + iconOffsetY);
            iconOffsetY += 20; // Incrementa o deslocamento para a próxima linha
            currentCount = 0;
        } else {
            addIconWithText(iconWhatsApp, String(user.whatsapp).trim(), iconX, iconY + iconOffsetY);
        }
    }

    if (hasUserLink(user.instagram)) {
        currentCount++;
        if (currentCount === 2) {
            doc.text("", iconX + iconSize + 40, iconY + iconOffsetY + 5);
            addIconWithText(iconInstagram, String(user.instagram).trim(), iconX + 80, iconY + iconOffsetY);
            iconOffsetY += 20; // Incrementa o deslocamento para a próxima linha
            currentCount = 0;
        } else {
            addIconWithText(iconInstagram, String(user.instagram).trim(), iconX, iconY + iconOffsetY);
        }
    }
    if (hasUserLink(user.tiktok)) {
        currentCount++;
        if (currentCount === 2) {
            doc.text("", iconX + iconSize + 40, iconY + iconOffsetY + 5);
            addIconWithText(iconTikTok, String(user.tiktok).trim(), iconX + 80, iconY + iconOffsetY);
            iconOffsetY += 20; // Incrementa o deslocamento para a próxima linha
            currentCount = 0;
        } else {
            addIconWithText(iconTikTok, String(user.tiktok).trim(), iconX, iconY + iconOffsetY);
        }
    }


    // Salvar PDF com nome correto (funciona no mobile e desktop)
    const fileName = `recibo-de-venda-${order.id}.pdf`;

    // Detectar se é mobile
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    try {
        if (isMobile) {
            // Método otimizado para mobile
            const pdfBlob = doc.output('blob');
            const url = URL.createObjectURL(pdfBlob);

            // Criar link de download com atributos específicos para mobile
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.setAttribute('target', '_blank');
            link.style.display = 'none';

            // Adicionar ao DOM, clicar e remover
            document.body.appendChild(link);

            // Usar setTimeout para garantir que o link seja processado
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);

                // Limpar URL após download
                setTimeout(() => URL.revokeObjectURL(url), 1000);
            }, 100);

            console.log(`PDF baixado no mobile: ${fileName}`);
        } else {
            // Método para desktop
            const pdfBlob = doc.output('blob');
            const url = URL.createObjectURL(pdfBlob);

            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.style.display = 'none';

            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setTimeout(() => URL.revokeObjectURL(url), 100);

            console.log(`PDF baixado no desktop: ${fileName}`);
        }
    } catch (error) {
        console.warn('Erro no download programático, usando método fallback:', error);
        // Fallback para o método original
        doc.save(fileName);
    }
};
